import { useState, useRef, useCallback, useEffect } from 'react';

export interface UseTwoTapConfirmOptions {
    timeoutMs?: number;
    onFirstTap?: (id: string) => void;
    onConfirm?: (id: string) => void;
    onCancel?: () => void;
}

export interface UseTwoTapConfirmReturn {
    pendingId: string | null;
    handleTap: (id: string, _getLabel?: (id: string) => string) => boolean;
    cancelPending: () => void;
    isPending: (id: string) => boolean;
}

/**
 * Two-tap confirmation pattern for young children.
 *
 * First tap on an item marks it as "pending" (speaking the label if provided).
 * Second tap on the SAME item confirms the action.
 * Tapping a DIFFERENT item switches the pending selection.
 * The pending state auto-cancels after timeoutMs.
 *
 * Accessibility (WCAG 2.2 AA): Screen reader users must understand the
 * pending/confirm state changes. The hook returns `pendingId` which indicates
 * the currently pending item. Consuming components should use
 * `accessibilityLiveRegion="polite"` on the element that announces the pending
 * state (e.g. "Tap again to confirm") so that screen readers announce the
 * state transition when `pendingId` changes. The `isPending(id)` return value
 * can be used to set `accessibilityState={{ selected: true }}` on the pending
 * item so screen readers convey which item is awaiting confirmation.
 */
export function useTwoTapConfirm(options: UseTwoTapConfirmOptions = {}): UseTwoTapConfirmReturn {
    const { timeoutMs = 6000, onFirstTap, onConfirm, onCancel } = options;
    const [pendingId, setPendingId] = useState<string | null>(null);
    const pendingIdRef = useRef<string | null>(null);
    const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const onFirstTapRef = useRef(onFirstTap);
    const onConfirmRef = useRef(onConfirm);
    const onCancelRef = useRef(onCancel);
    useEffect(() => {
        onFirstTapRef.current = onFirstTap;
        onConfirmRef.current = onConfirm;
        onCancelRef.current = onCancel;
    });

    const cancelPending = useCallback(() => {
        if (pendingTimerRef.current) {
            clearTimeout(pendingTimerRef.current);
            pendingTimerRef.current = null;
        }
        if (pendingIdRef.current !== null) {
            pendingIdRef.current = null;
            setPendingId(null);
            onCancelRef.current?.();
        }
    }, []);

    const handleTap = useCallback(
        (id: string, _getLabel?: (id: string) => string): boolean => {
            const currentPending = pendingIdRef.current;
            if (currentPending === id) {
                // Second tap — confirm.
                cancelPending();
                onConfirmRef.current?.(id);
                return true;
            }
            // First tap — mark as pending.
            if (pendingTimerRef.current) {
                clearTimeout(pendingTimerRef.current);
                pendingTimerRef.current = null;
            }
            pendingIdRef.current = id;
            setPendingId(id);
            onFirstTapRef.current?.(id);
            pendingTimerRef.current = setTimeout(() => {
                pendingIdRef.current = null;
                setPendingId(null);
                pendingTimerRef.current = null;
            }, timeoutMs);
            return false;
        },
        [timeoutMs, cancelPending]
    );

    const isPending = useCallback(
        (id: string) => pendingId === id,
        [pendingId]
    );

    useEffect(() => {
        return () => {
            if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
        };
    }, []);

    return { pendingId, handleTap, cancelPending, isPending };
}
